import type { StoreApi, UseBoundStore } from 'zustand';
import { create } from 'zustand';

import type { GlobalCopilotEntrypoint } from '../../telemetry/types';

type GlobalCopilotSessionMode = 'create' | 'preserve';

export interface CloseGlobalCopilotDrawerOptions {
  readonly createFreshSession: boolean;
}

export interface GlobalCopilotState {
  readonly isEnabled: boolean;
  readonly hasOpened: boolean;
  readonly hasPreparedSession: boolean;
  readonly isOpen: boolean;
  readonly entrypoint: GlobalCopilotEntrypoint | undefined;
  readonly sessionId: string;
  readonly prepareSession: () => void;
  readonly openDrawer: (entrypoint: GlobalCopilotEntrypoint, sessionMode?: GlobalCopilotSessionMode) => void;
  readonly close: (options?: CloseGlobalCopilotDrawerOptions) => void;
  readonly resetHostMount: () => void;
}

const createSessionId = (): string => crypto.randomUUID();

export const useGlobalCopilotStore: UseBoundStore<StoreApi<GlobalCopilotState>> = create<GlobalCopilotState>((set) => ({
  isEnabled: false,
  hasOpened: false,
  hasPreparedSession: false,
  isOpen: false,
  entrypoint: undefined,
  sessionId: createSessionId(),
  prepareSession: () => {
    set((state) => (state.isOpen ? state : { hasPreparedSession: true }));
  },
  openDrawer: (entrypoint, sessionMode = 'create') => {
    set((state) => ({
      hasOpened: true,
      hasPreparedSession: false,
      isOpen: true,
      entrypoint,
      sessionId: state.hasPreparedSession || sessionMode === 'preserve' ? state.sessionId : createSessionId(),
    }));
  },
  close: (options) => {
    set((state) => {
      if (!state.isOpen) {
        return state;
      }

      const nextSessionId = options?.createFreshSession ? createSessionId() : state.sessionId;

      return {
        isOpen: false,
        entrypoint: undefined,
        sessionId: nextSessionId,
      };
    });
  },
  resetHostMount: () => {
    set((state) => ({ hasOpened: state.isOpen, hasPreparedSession: false }));
  },
}));

export const enableGlobalCopilot = (): void => {
  useGlobalCopilotStore.setState({ isEnabled: true });
};

export const closeGlobalCopilotDrawer = (options?: CloseGlobalCopilotDrawerOptions): void => {
  useGlobalCopilotStore.getState().close(options);
};
